/**
 * Storage — where FWM's files are, and whether it is working.
 *
 * Private documents sit on the church's own one.com hosting; public media
 * follows wherever that is configured. This screen exists so nobody has to
 * take that on trust: it shows the pinned SSH fingerprint to check against
 * one.com, proves the connection by writing and reading a probe file, lists
 * anything held because a store could not be reached, and reports the nightly
 * document check.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, HardDrive, Loader2, RefreshCw, ShieldCheck, Clock,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, children, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b py-2 last:border-0">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="text-right text-sm font-medium text-slate-800" data-testid={testId}>
      {children}
    </dd>
  </div>
);

export default function AdminStoragePage() {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const { data, isLoading } = useQuery({
    queryKey: ['storage-status'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/storage/status`, { headers });
      if (!r.ok) throw new Error('Could not read the storage status');
      return r.json();
    },
    enabled: !!token,
    refetchInterval: 60000,
  });

  const act = useMutation({
    mutationFn: async ({ path, method = 'POST', ok }) => {
      const r = await fetch(`${API_URL}/api/admin/storage/${path}`, { method, headers });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'That did not work');
      return { out, ok };
    },
    onSuccess: ({ out, ok }) => {
      toast.success(ok || out.detail || 'Done');
      queryClient.invalidateQueries({ queryKey: ['storage-status'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const onecom = data?.onecom || {};
  const platform = data?.platform || {};
  const held = data?.held || {};
  const checks = data?.checks || [];

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6" data-testid="storage-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <HardDrive className="h-6 w-6 text-indigo-600" />
          Storage
        </h1>
        <p className="text-base text-slate-500">
          Where documents and images are kept, and whether they can be reached
        </p>
      </header>

      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />Looking…
        </p>
      ) : null}

      {onecom.root_refused ? (
        <div className="flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4"
             data-testid="storage-root-warning">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <p className="text-sm text-rose-800">{onecom.root_refused}</p>
        </div>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">one.com · private documents</CardTitle>
            <CardDescription>
              Invoices, quotations, student and HR records
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" data-testid="storage-probe-btn"
                  disabled={!onecom.configured || act.isPending}
                  onClick={() => act.mutate({
                    path: 'onecom/check', ok: 'Wrote and read back a probe file',
                  })}>
            {act.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-1.5 h-4 w-4" />}
            Test the connection
          </Button>
        </CardHeader>
        <CardContent>
          <dl>
            <Row label="In use for" testId="storage-private-backend">
              {data?.private_documents === 'onecom_sftp' ? (
                <Badge className="bg-emerald-100 text-emerald-800">one.com</Badge>
              ) : (
                <Badge variant="outline">platform store</Badge>
              )}
            </Row>
            <Row label="Server" testId="storage-host">{onecom.host || '—'}</Row>
            <Row label="Private folder" testId="storage-root">{onecom.root || '—'}</Row>
            <Row label="SSH fingerprint" testId="storage-fingerprint">
              <span className="font-mono text-xs">
                {onecom.host_key_fingerprint || 'not pinned yet'}
              </span>
              {onecom.host_key_source ? (
                <span className="ml-2 text-[11px] text-slate-500">
                  ({onecom.host_key_source === 'pinned'
                    ? 'trusted on first connection' : 'from configuration'})
                </span>
              ) : null}
            </Row>
          </dl>
          <p className="mt-3 text-xs text-slate-500">
            Check that fingerprint against one.com before loading confidential
            documents. If the server ever presents a different key, uploads stop
            rather than trusting it.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Platform store · fallback</CardTitle>
          <CardDescription>
            Only used if one.com cannot be reached — public media goes there too now
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl>
            <Row label="Status" testId="storage-platform-status">
              {platform.ok ? (
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />working
                </span>
              ) : (
                <span className="flex items-center gap-1 text-rose-700">
                  <AlertTriangle className="h-4 w-4" />not available
                </span>
              )}
            </Row>
          </dl>
          {!platform.ok && platform.detail ? (
            <p className="mt-2 text-xs text-rose-700" data-testid="storage-platform-detail">
              {platform.detail}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Waiting to be sent on</CardTitle>
            <CardDescription>
              Files held, encrypted, while a store could not be reached
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" data-testid="storage-flush-btn"
                  disabled={!held.waiting || act.isPending}
                  onClick={() => act.mutate({ path: 'flush', ok: 'Sent on' })}>
            Send them now
          </Button>
        </CardHeader>
        <CardContent>
          {held.waiting ? (
            <ul className="space-y-1 text-sm" data-testid="storage-held-list">
              {(held.oldest || []).map((f) => (
                <li key={f.path} className="flex justify-between gap-4">
                  <span className="truncate text-slate-700">{f.path}</span>
                  <span className="shrink-0 text-xs text-slate-500">
                    {f.attempts ? `${f.attempts} attempts · ` : ''}
                    {Math.round((f.size || 0) / 1024)} KB
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500" data-testid="storage-held-empty">
              Nothing is waiting — every upload reached a store.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              Nightly document check
            </CardTitle>
            <CardDescription>
              Every night at 02:20, each document is confirmed present and unchanged
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" data-testid="storage-check-btn"
                  disabled={!onecom.configured || act.isPending}
                  onClick={() => act.mutate({
                    path: 'check-now', ok: 'Checking — refresh in a moment',
                  })}>
            Check now
          </Button>
        </CardHeader>
        <CardContent>
          {data?.check_overdue ? (
            <p className="mb-3 flex items-center gap-2 text-sm text-amber-700"
               data-testid="storage-check-overdue">
              <Clock className="h-4 w-4" />
              Nothing has checked the documents in the last day and a half.
            </p>
          ) : null}
          {checks.length ? (
            <ul className="space-y-1 text-sm" data-testid="storage-check-list">
              {checks.map((c) => (
                <li key={c.started_at} className="flex justify-between gap-4 border-b py-1 last:border-0">
                  <span className="text-slate-600">
                    {new Date(c.started_at).toLocaleString()}
                  </span>
                  <span className={c.ok ? 'text-emerald-700' : 'text-rose-700'}>
                    {c.checked} checked · {c.verified} verified
                    {c.missing ? ` · ${c.missing} missing` : ''}
                    {c.altered ? ` · ${c.altered} altered` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500" data-testid="storage-check-empty">
              No check has run yet.
            </p>
          )}
          <p className="mt-3 text-xs text-slate-500">
            The check writes a dated manifest of every file, its size and its
            hash, beside the documents on one.com. It is not a second copy
            elsewhere — an off-site backup needs a destination you provide.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
